import React, { useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { levelFor } from "../lib/levels";
import { toast } from "sonner";
import { Check, Eye, Ear, Hand, Sparkles, Coffee } from "lucide-react";

const STEPS = [
  { n: 5, label: "things you can SEE", icon: Eye, prompt: "Name 5 things you can see right now" },
  { n: 4, label: "things you can TOUCH", icon: Hand, prompt: "Name 4 things you can touch" },
  { n: 3, label: "things you can HEAR", icon: Ear, prompt: "Name 3 things you can hear" },
  { n: 2, label: "things you can SMELL", icon: Sparkles, prompt: "Name 2 things you can smell" },
  { n: 1, label: "thing you can TASTE", icon: Coffee, prompt: "Name 1 thing you can taste" },
];

export default function Grounding() {
  const { user, setUser } = useAuth();
  const lvl = levelFor(user?.current_level || 1);
  const [stepIdx, setStepIdx] = useState(0);
  const [inputs, setInputs] = useState(STEPS.map(s => Array(s.n).fill("")));
  const [done, setDone] = useState(false);

  const setVal = (si, i, v) => {
    setInputs(prev => {
      const c = prev.map(arr => [...arr]);
      c[si][i] = v;
      return c;
    });
  };

  const complete = async () => {
    try {
      const { data } = await api.post("/exercise/grounding/complete");
      if (data.user) setUser(data.user);
      setDone(true);
      toast.success(data.awarded ? "+20 XP · Stillness +1" : "Already counted today.");
    } catch { toast.error("Couldn't save."); }
  };

  const step = STEPS[stepIdx];
  const Icon = step.icon;
  const filled = inputs[stepIdx].filter(x => x.trim()).length === step.n;

  return (
    <div className="animate-fadeUp" data-testid="grounding-page">
      <div className="text-xs font-bold uppercase tracking-[0.25em] opacity-60">Exercise</div>
      <h1 className="font-display font-black text-4xl tracking-tighter mt-2">5 · 4 · 3 · 2 · 1</h1>
      <p className="opacity-70 mt-2 max-w-xl">Pull your nervous system back into the room. One sense at a time.</p>

      <div className="mt-6 flex gap-2">
        {STEPS.map((s, i) => (
          <div key={s.label} className={`h-1.5 flex-1 rounded-full ${i <= stepIdx ? "bg-[#0e203b]" : "bg-[#0e203b]/10"}`} />
        ))}
      </div>

      {!done ? (
        <div className="mt-8 rounded-3xl p-7 sm:p-10 shadow-lg border border-[#0e203b]/5" style={{ backgroundColor: lvl.surface }}>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: lvl.accent }}>
              <Icon size={28} strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-black text-3xl tracking-tighter">{step.n} {step.label}</div>
              <div className="text-sm opacity-70 mt-1">{step.prompt}</div>
            </div>
          </div>
          <div className="mt-6 space-y-2">
            {inputs[stepIdx].map((v, i) => (
              <input
                key={`${step.label}-${i}`}
                data-testid={`ground-input-${stepIdx}-${i}`}
                value={v}
                onChange={e => setVal(stepIdx, i, e.target.value)}
                placeholder={`#${i + 1}`}
                className="w-full h-12 px-4 rounded-2xl border-2 border-[#0e203b]/10 focus:border-[#0e203b] outline-none font-medium bg-white"
              />
            ))}
          </div>
          <div className="mt-6 flex justify-between">
            <button onClick={() => setStepIdx(s => Math.max(0, s - 1))} disabled={stepIdx === 0} data-testid="ground-back" className="btn-pill bg-white border-2 border-[#0e203b]/10 rounded-full px-5 h-12 font-bold disabled:opacity-40">Back</button>
            {stepIdx < STEPS.length - 1 ? (
              <button onClick={() => setStepIdx(s => s + 1)} disabled={!filled} data-testid="ground-next" className="btn-pill bg-[#0e203b] text-white rounded-full px-7 h-12 font-bold disabled:opacity-40">Next</button>
            ) : (
              <button onClick={complete} disabled={!filled} data-testid="ground-complete" className="btn-pill bg-[#0e203b] text-white rounded-full px-7 h-12 font-bold disabled:opacity-40">Finish</button>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-8 rounded-3xl p-10 text-center shadow-lg border border-[#0e203b]/5" style={{ backgroundColor: lvl.surface }}>
          <Check size={48} className="mx-auto" />
          <div className="font-display font-black text-3xl tracking-tighter mt-3">Back in the room.</div>
          <div className="opacity-70 mt-2">Rep counted. +20 XP.</div>
        </div>
      )}
    </div>
  );
}
